import { GroupBase, chakraComponents } from "chakra-react-select";
import V2Image from "components/V2Image";
import {
  AsyncSmartSelect,
  AsyncSmartSelectProps,
  defaultOptionStyles,
} from "components/SmartSelect";
import { fancyBearsSupply } from "config";
import useTranslate from "hooks/useTranslate";

type FancyBearSelectOption = {
  label: string;
  value?: number;
  options?: FancyBearSelectOption[];
};

type FancyBearSelectProps = Omit<
  AsyncSmartSelectProps<
    FancyBearSelectOption,
    false,
    GroupBase<FancyBearSelectOption>
  >,
  | "onChange"
  | "value"
  | "defaultOptions"
  | "loadOptions"
  | "components"
  | "blurInputOnSelect"
> & {
  tokenId?: number;
  ownedTokens?: number[];
  firstTokenId?: number;
  lastTokenId?: number;
  optionPrefix?: string;
  onChange?: (tokenId?: number) => void;
};

const generateOptions = (
  inputValue: string = "1",
  firstTokenId: number,
  lastTokenId: number,
  optionPrefix: string
): FancyBearSelectOption[] => {
  const options = [];
  const valueAsNumber = parseInt(inputValue, 10);

  if (!Number.isInteger(valueAsNumber)) {
    return [];
  }

  const generateOptionsFrom = Math.max(
    Math.min(valueAsNumber, lastTokenId),
    firstTokenId
  );

  let counter = 0;
  let tokenId = generateOptionsFrom;
  while (tokenId < lastTokenId && counter <= 5) {
    options.push({
      value: tokenId,
      label: `${optionPrefix}${tokenId}`,
    });
    tokenId = parseInt(`${generateOptionsFrom}${counter}`);
    counter++;
  }

  if (options.length <= 1) {
    counter = 1;
    tokenId = generateOptionsFrom + 1;
    while (tokenId < lastTokenId && counter <= 5) {
      options.push({
        value: tokenId,
        label: `${optionPrefix}${tokenId}`,
      });
      tokenId++;
      counter++;
    }
  }

  return options;
};

const FancyBearSelect = ({
  tokenId,
  ownedTokens = [],
  firstTokenId = 1,
  lastTokenId = fancyBearsSupply,
  optionPrefix = "#",
  chakraStyles = {},
  onChange,
  ...rest
}: FancyBearSelectProps) => {
  const translate = useTranslate();

  const defaultOptions = [
    {
      label: translate("bearSelect:owned"),
      options: ownedTokens
        .map((tokenId) => ({
          label: `${optionPrefix}${tokenId}`,
          value: tokenId,
        }))
        .sort((a, b) => a.value - b.value),
    },
    {
      label: ownedTokens.length
        ? translate("bearSelect:other")
        : translate("bearSelect:community"),
      options: generateOptions(
        tokenId?.toString(),
        firstTokenId,
        lastTokenId,
        optionPrefix
      ).slice(1),
    },
  ];

  return (
    <AsyncSmartSelect<
      FancyBearSelectOption,
      false,
      GroupBase<FancyBearSelectOption>
    >
      value={tokenId ? { value: tokenId, label: `#${tokenId}` } : undefined}
      placeholder={translate("bearSelect:placeholder")}
      components={{
        Option: ({ children, ...props }) => (
          <chakraComponents.Option {...props}>
            <V2Image
              tokenId={props.data.value!}
              size="256"
              w="8"
              h="8"
              flex="none"
              mr="3"
              borderRadius="full"
            />

            {children}
          </chakraComponents.Option>
        ),
        Input: ({ children, type, ...props }) => (
          <chakraComponents.Input
            type="number"
            min={firstTokenId}
            max={lastTokenId}
            {...props}
          />
        ),
      }}
      chakraStyles={{
        option: (provided) => ({
          ...provided,
          ...defaultOptionStyles,
          px: "3",
          py: "1.5",
        }),
        ...chakraStyles,
      }}
      defaultOptions={defaultOptions}
      loadOptions={(inputValue: string) =>
        Promise.resolve(
          generateOptions(inputValue, firstTokenId, lastTokenId, optionPrefix)
        )
      }
      onChange={(option) => onChange && onChange(option?.value)}
      blurInputOnSelect
      {...rest}
    />
  );
};

export default FancyBearSelect;
